import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Vote, Check, Clock, Trophy, Lock, Radio, MessageCircle, Send, Flame } from 'lucide-react';
import { Input } from './ui/input';
import { WaveformPlayer } from './WaveformPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const useCountdown = (endIso) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    if (!endIso) return;
    const tick = () => {
      const diff = new Date(endIso).getTime() - Date.now();
      if (diff <= 0) return setRemaining('Closed');
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      if (days > 0) setRemaining(`${days}d ${hours}h left`);
      else if (hours > 0) setRemaining(`${hours}h ${mins}m left`);
      else setRemaining(`${mins}m left`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [endIso]);
  return remaining;
};

export const VotingWidget = () => {
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [voting, setVoting] = useState(false);
  const [pendingSnippetId, setPendingSnippetId] = useState(null);

  const remaining = useCountdown(round?.ends_at);
  const isActive = round?.status === 'active';
  const winner = round?.winner_snippet_id;

  const fetchRound = async (voterEmail = '') => {
    try {
      const { data } = await axios.get(`${API_URL}/voting/current`, {
        params: voterEmail ? { email: voterEmail } : {},
      });
      setRound(data.round);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRound(); }, []);

  const handleVote = async (snippetId) => {
    if (!email.trim()) {
      toast.error('Enter your email to lock in your vote');
      return;
    }
    setVoting(true);
    setPendingSnippetId(snippetId);
    try {
      const { data } = await axios.post(`${API_URL}/voting/vote`, {
        round_id: round.id,
        snippet_id: snippetId,
        email: email.trim(),
        name: name.trim(),
      });
      setRound(data.round);
      toast.success(data.message || 'Vote counted!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? 'Please enter a valid email' : (detail || 'Vote failed');
      toast.error(msg);
    } finally {
      setVoting(false);
      setPendingSnippetId(null);
    }
  };

  if (loading || !round) return null;

  return (
    <section className="py-16 relative overflow-hidden" data-testid="voting-section">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-cyan-950/20 to-black" />
      <div className="container relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              {isActive ? <Radio className="w-5 h-5 text-white" /> : <Trophy className="w-5 h-5 text-white" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">
                  {isActive ? 'Fan Vote — Live Now' : 'Fan Vote — Closed'}
                </span>
                {isActive && remaining && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" /> {remaining}
                  </span>
                )}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">{round.title}</h2>
              {round.description && (
                <p className="text-sm md:text-base text-gray-400 mt-1">{round.description}</p>
              )}
            </div>
          </div>

          {isActive && !round.user_vote_snippet_id && (
            <div
              className="glass-card p-4 mb-4 flex flex-col sm:flex-row gap-3 border border-cyan-500/20"
              data-testid="voting-identity-form"
            >
              <Input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-dark sm:max-w-[180px]"
                data-testid="voting-name-input"
              />
              <Input
                type="email"
                placeholder="you@email.com to lock your vote"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark flex-1"
                data-testid="voting-email-input"
              />
            </div>
          )}

          <div className="space-y-3" data-testid="voting-snippet-list">
            {round.snippets.map((snippet, idx) => {
              const isMyVote = round.user_vote_snippet_id === snippet.id;
              const isWinner = !isActive && winner === snippet.id;
              const isLoading = voting && pendingSnippetId === snippet.id;
              const isTrending = isActive && snippet.is_trending;
              return (
                <div
                  key={snippet.id}
                  className={`glass-card p-4 md:p-5 border transition-all ${
                    isWinner
                      ? 'border-yellow-400/50 bg-gradient-to-r from-yellow-500/10 to-orange-500/5'
                      : isMyVote
                      ? 'border-cyan-400/50 bg-cyan-500/5'
                      : isTrending
                      ? 'border-orange-400/40 bg-gradient-to-r from-orange-500/10 to-red-500/5'
                      : 'border-white/5'
                  }`}
                  data-testid={`voting-snippet-${idx + 1}`}
                >
                  <div className="flex items-start gap-4">
                    {snippet.cover_url ? (
                      <img
                        src={snippet.cover_url}
                        alt={snippet.title}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center flex-shrink-0">
                        <Vote className="w-6 h-6 text-white/60" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-white truncate">{snippet.title}</h3>
                        {isTrending && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gradient-to-r from-orange-500/30 to-red-500/30 text-orange-200 border border-orange-400/40 uppercase tracking-wider font-semibold animate-pulse"
                            data-testid={`voting-trending-${idx + 1}`}
                          >
                            <Flame className="w-3 h-3" /> Trending
                          </span>
                        )}
                        {isWinner && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 uppercase tracking-wider font-semibold">
                            <Trophy className="w-3 h-3" /> Winner
                          </span>
                        )}
                        {isMyVote && isActive && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 uppercase tracking-wider font-semibold">
                            <Check className="w-3 h-3" /> Your Vote
                          </span>
                        )}
                      </div>
                      {snippet.description && (
                        <p className="text-sm text-gray-400 line-clamp-2 mb-2">{snippet.description}</p>
                      )}
                      {snippet.audio_url && (
                        <div className="mb-2" data-testid={`voting-snippet-audio-${idx + 1}`}>
                          <WaveformPlayer
                            src={snippet.audio_url}
                            testIdPrefix={`voting-waveform-${idx + 1}`}
                          />
                        </div>
                      )}

                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">{snippet.vote_count} votes</span>
                          <span className="text-gray-400 font-semibold">{snippet.vote_percentage}%</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              isWinner
                                ? 'bg-gradient-to-r from-yellow-400 to-orange-400'
                                : isMyVote
                                ? 'bg-gradient-to-r from-cyan-400 to-purple-400'
                                : 'bg-white/20'
                            }`}
                            style={{ width: `${snippet.vote_percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {isActive ? (
                      <button
                        onClick={() => handleVote(snippet.id)}
                        disabled={voting}
                        className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition ${
                          isMyVote
                            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                        } disabled:opacity-50`}
                        data-testid={`voting-vote-button-${idx + 1}`}
                      >
                        {isLoading ? '...' : isMyVote ? 'Voted' : 'Vote'}
                      </button>
                    ) : (
                      <div className="flex-shrink-0 text-gray-500">
                        <Lock className="w-5 h-5" />
                      </div>
                    )}
                  </div>

                  <SnippetComments
                    roundId={round.id}
                    snippetId={snippet.id}
                    snippetIdx={idx}
                    commentCount={snippet.comment_count || 0}
                    canPost={isActive}
                    defaultName={name}
                    defaultEmail={email}
                  />
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            {isActive
              ? 'You can change your vote until the round closes. Voters auto-join the First-Listen list.'
              : 'This round is closed. Watch for the next one.'}
          </p>
        </div>
      </div>
    </section>
  );
};


const SnippetComments = ({ roundId, snippetId, snippetIdx, commentCount, canPost, defaultName, defaultEmail }) => {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [posting, setPosting] = useState(false);
  const [localCount, setLocalCount] = useState(commentCount);

  useEffect(() => {
    if (defaultEmail && !email) setEmail(defaultEmail);
    if (defaultName && !name) setName(defaultName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, defaultName]);

  useEffect(() => { setLocalCount(commentCount); }, [commentCount]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/voting/rounds/${roundId}/comments`, {
        params: { snippet_id: snippetId, limit: 20 },
      });
      setComments(data.comments || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && comments.length === 0) loadComments();
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return toast.error('Write something first');
    if (!email.trim()) return toast.error('Enter your email to post');
    setPosting(true);
    try {
      const { data } = await axios.post(`${API_URL}/voting/comment`, {
        round_id: roundId,
        snippet_id: snippetId,
        email: email.trim(),
        name: name.trim(),
        comment: text.trim(),
      });
      setComments((prev) => [data.comment, ...prev]);
      setLocalCount((c) => c + 1);
      setText('');
      toast.success(data.added_to_waitlist ? 'Posted! You joined the First-Listen list too.' : 'Posted!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? 'Check your email and comment' : detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`voting-comments-${snippetIdx + 1}`}>
      <button
        onClick={handleToggle}
        className="text-xs text-gray-400 hover:text-cyan-300 flex items-center gap-1.5 transition"
        data-testid={`voting-comments-toggle-${snippetIdx + 1}`}
      >
        <MessageCircle className="w-3.5 h-3.5" />
        {localCount === 0 ? (canPost ? 'Leave a reaction' : 'No reactions') : `${localCount} reaction${localCount === 1 ? '' : 's'}`}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {canPost && (
            <form onSubmit={handlePost} className="space-y-2" data-testid={`voting-comment-form-${snippetIdx + 1}`}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Your take on this snippet..."
                className="input-dark w-full text-sm resize-y"
                data-testid={`voting-comment-input-${snippetIdx + 1}`}
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="input-dark flex-1 text-sm"
                  data-testid={`voting-comment-name-${snippetIdx + 1}`}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="input-dark flex-1 text-sm"
                  required
                  data-testid={`voting-comment-email-${snippetIdx + 1}`}
                />
                <button
                  type="submit"
                  disabled={posting || !text.trim() || !email.trim()}
                  className="btn-primary text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                  data-testid={`voting-comment-submit-${snippetIdx + 1}`}
                >
                  <Send className="w-3.5 h-3.5" />
                  {posting ? '...' : 'Post'}
                </button>
              </div>
            </form>
          )}

          <div className="space-y-2 max-h-72 overflow-y-auto">
            {loading ? (
              <p className="text-xs text-gray-500">Loading...</p>
            ) : comments.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                {canPost ? 'Be the first to react.' : 'No reactions on this snippet.'}
              </p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="rounded-lg bg-white/5 px-3 py-2 text-sm" data-testid={`voting-comment-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
                    <span className="font-semibold text-white">{c.name || 'Anonymous'}</span>
                    <span>&middot;</span>
                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-200 whitespace-pre-wrap break-words">{c.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
